import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Marina, Role, User } from 'src/app/models';
import { Application } from 'src/app/models/enums/application';
import { MenuItem } from 'src/app/models/menu-item';
import { UserMarina } from 'src/app/models/user-marina';
import { AccessControlService, CapabilityDTO } from 'src/app/services/access-control.service';
import { MenuItemService } from 'src/app/services/menu-item.service';
import { RoleService } from 'src/app/services/role.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { StorageUtil } from 'src/app/utils/storage.util';
import Swal from 'sweetalert2';
import {OperationalConfigService} from "../../../../services/config/operational-config.service";

@Component({
  selector: 'app-user-role-form',
  templateUrl: './user-role-form.component.html',
  styleUrls: ['./user-role-form.component.scss']
})
export class UserRoleFormComponent implements OnInit {

  maskNumber = ['/[1-9]/'];
  role: Role = new Role();
  hierarchyList: any[] = [];
  menuItensListWeb: any[] = [];
  menuItensListGestor: any[] = [];
  user: User;
  roleForm: UntypedFormGroup;
  isMovement: boolean = false;

  /**
   * EAS-327 — capacidades (ações) do perfil. Saíram do cadeado por usuário e passaram a ser
   * configuradas aqui. Perfil-padrão do sistema tem recorte fixo: exibe, não edita. Só aparece com
   * o EAS-327 ligado no backend (sem ele a seção fica vazia e nada é gravado).
   */
  capabilityCategories: Array<{ name: string; caps: CapabilityDTO[] }> = [];
  selectedCapabilities: { [code: string]: boolean } = {};
  capabilitiesReadOnly = false;
  /** O perfil já tinha capacidade configurada ao abrir: a primeira configuração é a que restringe. */
  private hadCapabilitiesOnLoad = false;
  /**
   * Só gravamos as capacidades se o usuário mexeu nelas. Salvar o perfil por outro motivo (renomear,
   * mexer no menu) **não pode** reescrever a configuração de permissão — antes disso, um save
   * comum enviava a lista vazia e apagava o que estava configurado.
   */
  capabilitiesDirty = false;

  constructor(
    public dialogRef: DynamicDialogRef,
    private roleService: RoleService,
    private messageUtil: MessageUtil,
    private spinner: NgxSpinnerService,
    private menuItemService: MenuItemService,
    private userService: UserService,
    private toasty: ToastService,
    private config: DynamicDialogConfig,
    private operationalConfigService: OperationalConfigService,
    private accessService: AccessControlService
  ) { }

  async ngOnInit(): Promise<void> {
    if (this.config.data && this.config.data.role) {
      this.role = this.config.data.role;
      this.role.menuItens = this.role.menuItens.map((r) => (new MenuItem(r.id, r.application)));
    }
    this.startForm();
    // EAS-327: carrega as permissões de ação ANTES da montagem do menu, de propósito. As promises
    // de menu só resolviam no caminho de sucesso, então uma falha ali (ex.: usuário sem perfil na
    // marina, que faz /secure/menu/application-structure devolver 500) travava o resto do ngOnInit
    // e a seção de permissões simplesmente não aparecia.
    this.loadCapabilities();
    await this.findAllEasyWeb();
    await this.findAllEasyGestor();
    await this.mapRoleMenuitens();
    this.getHierarchyList();
    this.operationalConfigService.isMovements().then((isMovement: boolean) => {
      this.isMovement = isMovement;
    });
  }

  async getHierarchyList(): Promise<void> {
    const userMarina = await this.findUserMarina();
    if(userMarina) {
      const list = [];
      for (let index = userMarina.roleId.hierarchy; index < 10; index++) {
        list.push(index);
      }
      this.hierarchyList = [...list]
        .map((hierarchy) => ({ label: '' + hierarchy, value: hierarchy }));
      if (!userMarina.roleId.administrator) {
        this.roleForm.get("administrator").disable();
      }
    }
  }

  findUserMarina(): Promise<UserMarina>{
    return new Promise(resolve => {
      this.userService.getUserMarina().subscribe(userMarina => {
        resolve(userMarina);
      });
    })
  }

  updateMenuItens(): void {
    const menuItens = [...this.roleForm.get('selectedItensGestor').value.concat([...this.roleForm.get('selectedItensWeb').value])];
    this.roleForm.get('menuItens').setValue(menuItens);
  }

  async findAllEasyWeb(): Promise<void> {
    return new Promise<void>(
      async (res) => {
        this.menuItemService.getAllStructuredByAplication(Application.EasyWeb).subscribe({
          next: async (result) => {
            this.mapMenuChilds('', this.menuItensListWeb, result);
            this.menuItensListWeb = [...this.menuItensListWeb].map(
              (m) => ({ label: m.id + ' - ' + this.messageUtil.translateKey(m.label), value: new MenuItem(m.id, Application.EasyWeb) })
            );
            res();
          },
          // sem isto a promise nunca resolve e o restante do ngOnInit não roda
          error: () => res()
        });

      }
    );
  }

  mapMenuChilds(parentLabel, list: any[], childsList: any[]): void {
    childsList.forEach((element) => {
      element.label = this.messageUtil.translateKey(element.label);
      if (element.children && element.children.length > 0) {
        element.disabled = true;
        list.push(element);
        element.label = ((parentLabel && parentLabel !== '') ? parentLabel + ' > ' : '') + element.label;
        this.mapMenuChilds(element.label, list, element.children);
      } else {
        element.label = ((parentLabel && parentLabel !== '') ? parentLabel + ' > ' : '') + element.label;
        list.push(element);
      }
    });
  }


  async findAllEasyGestor(): Promise<void> {
    return new Promise<void>(
      async (res) => {
        this.menuItemService.getAllStructuredByAplication(Application.GestorApp).subscribe({
          next: async (result) => {
            this.menuItensListGestor = [...result]
              .map(
                (m) => (
                  {
                    label: m.id + ' - ' + this.messageUtil.translateKey(m.label),
                    value: new MenuItem(m.id, Application.GestorApp)
                  }
                )
              );
            res();
          },
          error: () => res()
        });
      }
    );
  }

  async mapRoleMenuitens(): Promise<void> {
    return new Promise<void>(
      async (res) => {
        const web = [...this.roleForm.get('selectedItensWeb').value];
        const gestor = [...this.roleForm.get('selectedItensGestor').value];
        this.role.menuItens.forEach((element) => {
          if (element.application === Application.EasyWeb) {
            web.push(element);
          } else if (element.application === Application.GestorApp) {
            gestor.push(element);
          }
        });
        this.roleForm.get('selectedItensWeb').setValue([...web]);
        this.roleForm.get('selectedItensGestor').setValue([...gestor]);
        res();
      }
    );
  }

  // ---------- EAS-327: capacidades do perfil ----------

  private loadCapabilities(): void {
    this.accessService.roleCapabilitiesEnabled().subscribe((enabled) => {
      if (enabled) {
        this.loadCapabilityCatalog();
      }
    });
  }

  private loadCapabilityCatalog(): void {
    this.capabilitiesReadOnly = !!this.role.systemDefault;
    this.accessService.getCatalog().subscribe({
      next: (catalog) => {
        this.capabilityCategories = this.groupCapabilitiesByCategory(catalog || []);
        (catalog || []).forEach((c) => (this.selectedCapabilities[c.code] = false));
        if (!this.role.id) {
          return;   // perfil novo: nenhuma capacidade marcada
        }
        this.accessService.getRoleAccess(this.role.id).subscribe({
          next: (access) => {
            const codes = (access && access.capabilityCodes) || [];
            codes.forEach((code) => (this.selectedCapabilities[code] = true));
            this.hadCapabilitiesOnLoad = !this.role.systemDefault && codes.length > 0;
          },
          error: () => { /* perfil sem capacidade configurada: tudo desmarcado */ }
        });
      },
      error: () => { this.capabilityCategories = []; }
    });
  }

  private groupCapabilitiesByCategory(catalog: CapabilityDTO[]): Array<{ name: string; caps: CapabilityDTO[] }> {
    const groups = new Map<string, CapabilityDTO[]>();
    catalog.forEach((c) => {
      if (!groups.has(c.category)) {
        groups.set(c.category, []);
      }
      groups.get(c.category).push(c);
    });
    return Array.from(groups.entries()).map(([name, caps]) => ({ name, caps }));
  }

  capabilityCategoryIcon(name: string): string {
    const n = (name || '').toLowerCase();
    if (n.includes('fatur')) { return 'pi-dollar'; }
    if (n.includes('cadastr')) { return 'pi-id-card'; }
    if (n.includes('usu')) { return 'pi-users'; }
    if (n.includes('config')) { return 'pi-cog'; }
    return 'pi-folder';
  }

  /**
   * O catálogo vem do backend em CAIXA ALTA e sem acento (USUARIOS, CONFIGURACOES) — aqui
   * normalizamos para exibição.
   */
  capabilityCategoryLabel(name: string): string {
    const normalized = (name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
    const labels: { [key: string]: string } = {
      FATURAMENTO: 'Faturamento',
      CADASTRO: 'Cadastro',
      USUARIOS: 'Usuários',
      CONFIGURACOES: 'Configurações'
    };
    if (labels[normalized]) {
      return labels[normalized];
    }
    return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : name;
  }

  selectedCapabilityCount(group: { name: string; caps: CapabilityDTO[] }): number {
    return group.caps.filter((c) => this.selectedCapabilities[c.code]).length;
  }

  toggleCapabilityCategory(group: { name: string; caps: CapabilityDTO[] }, value: boolean): void {
    if (this.capabilitiesReadOnly) {
      return;
    }
    group.caps.forEach((c) => (this.selectedCapabilities[c.code] = value));
    this.markCapabilitiesDirty();
  }

  /** Chamado por qualquer alteração na seção de permissões de ação. */
  markCapabilitiesDirty(): void {
    if (!this.capabilitiesReadOnly) {
      this.capabilitiesDirty = true;
    }
  }

  private selectedCapabilityCodes(): string[] {
    return Object.keys(this.selectedCapabilities).filter((code) => this.selectedCapabilities[code]);
  }

  /** Total de capacidades do catálogo (para dizer quantas ficam de fora). */
  get totalCapabilities(): number {
    return this.capabilityCategories.reduce((total, group) => total + group.caps.length, 0);
  }

  /**
   * EAS-327 — a primeira marcação num perfil que não tinha nada configurado **restringe**: antes
   * ele não tinha recorte de capacidade (nada bloqueado), depois passa a valer exatamente o que foi
   * marcado. É contraintuitivo o suficiente para merecer confirmação — o admin marca 3 caixas
   * pensando "liberar estas" e na verdade corta as outras 23.
   */
  private confirmFirstRestriction(): Promise<boolean> {
    const marked = this.selectedCapabilityCodes().length;
    const restricting = this.capabilitiesDirty && !this.capabilitiesReadOnly
      && !this.hadCapabilitiesOnLoad && marked > 0;
    if (!restricting) {
      return Promise.resolve(true);
    }
    return Swal.fire({
      title: 'Confirmar permissões de ação?',
      text: `Este perfil passará a permitir somente as ${marked} ações marcadas — as outras `
        + `${this.totalCapabilities - marked} ficam bloqueadas para quem usa este perfil. `
        + `Vale no próximo acesso de cada usuário.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Revisar',
      reverseButtons: true
    }).then((result) => !!result.value);
  }

  /**
   * Grava as capacidades do perfil depois do perfil em si. Perfil-padrão do sistema não é
   * configurável, então nem chamamos o endpoint.
   */
  private saveCapabilities(roleId: number, done: () => void): void {
    // Não tocou na seção → não encosta na configuração existente.
    if (this.capabilitiesReadOnly || !roleId || !this.capabilitiesDirty) {
      done();
      return;
    }
    this.accessService.setRoleAccess(roleId, this.selectedCapabilityCodes()).subscribe({
      next: () => done(),
      error: () => {
        this.spinner.hide();
        Swal.fire('Atenção',
          'O perfil foi salvo, mas não foi possível gravar as permissões de ação. Reabra o perfil e tente novamente.',
          'warning');
        this.dialogRef.close();
      }
    });
  }

  changePhotos(e): void {
    this.roleForm.get('photos').setValue(e.checked);
  }

  changeAdministrator(e): void {
    this.roleForm.get('administrator').setValue(e.checked);
  }

  changeInvoiceDatesPermission(e): void {
    this.roleForm.get('changeInvoiceDates').setValue(e.checked);
  }

  changeResponsible(e): void {
    this.roleForm.get('responsible').setValue(e.checked);
  }
  
  changeDocMaster(e): void {
    this.roleForm.get('docMaster').setValue(e.checked);
  }

  empetyList(list): boolean {
    if (!list || list.length === 0) {
      return false;
    }
    return true;

  }

  async save(): Promise<void> {
    // EAS-327: confirma antes de aplicar a primeira restrição de capacidade neste perfil.
    if (!await this.confirmFirstRestriction()) {
      return;
    }
    this.spinner.show();
    this.roleService.save(this.roleForm.value).subscribe({
      next: (data) => {
        const isEdit = !!this.roleForm.value.id;
        // EAS-327: em perfil novo o id só existe depois do save do perfil.
        const roleId = this.roleForm.value.id || (data && data.id);
        this.saveCapabilities(roleId, () => {
          this.spinner.hide();
          this.toasty.success(isEdit ? 'Perfil alterado com sucesso!' : 'Perfil salvo com sucesso!');
          this.dialogRef.close();
        });
      },
      error: (error) => {
        this.spinner.hide();
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      },
      complete: () => {
        this.spinner.hide();
      }
    });
  }

  formValidations(): boolean {
    if (!this.roleForm.value.hierarchy) {
      this.removedHierarchyFromForm();
    }
    if (this.roleForm.valid) {
      return true;
    } else {
      Swal.fire('Alerta!', 'Por favor, preencha os campos obrigatórios', 'warning');
      return false;
    }
  }

  startForm(): void { 
    this.roleForm = new UntypedFormGroup({
      id: new UntypedFormControl(this.role.id),
      name: new UntypedFormControl(this.role.name, Validators.required),
      hierarchy: new UntypedFormControl(this.role.hierarchy, Validators.required),
      menuItens: new UntypedFormControl(this.role.menuItens),
      selectedItensWeb: new UntypedFormControl([]),
      selectedItensGestor: new UntypedFormControl([]),
      changeInvoiceDates: new UntypedFormControl(this.role.changeInvoiceDates),
      marina: new UntypedFormControl({ id: StorageUtil.getMarina().id }, Validators.required),
      photos: new UntypedFormControl(this.role.photos, Validators.required),
      administrator: new UntypedFormControl(this.role.administrator),
      responsible: new UntypedFormControl(this.role.responsible),
      docMaster: new UntypedFormControl(this.role.docMaster)
    });
  }

  removedHierarchyFromForm(): void {
    if (this.roleForm.value.name.toUpperCase() === 'ADMIN' || this.roleForm.value.name.toUpperCase() === 'GESTOR') {
      this.roleForm.get('hierarchy').setValue(1);
    } else {
      this.roleForm.get('hierarchy').setValue(2);
    }
  }

}
